/* Prefill for the IRS business name control: the first four significant
   characters of the business name, for every business type. This is only a
   starting value - the control the IRS assigned to the EIN (printed on the
   filer's CP 575 letter) is what the IRS matches, and the filer can overwrite it.
   Sole proprietors included: IRS Pub 4164 Exhibit 4 d) says a sole proprietor's
   control comes from the last name, but on prod every sole proprietor the IRS
   accepted (5 of 5, Aug-Sep 2026, e.g. "Nathan L Thies" -> NATH) was accepted
   with the first four letters of the name as entered, so that is the default.
   Exhibit 1: omit a leading "The" only when more than one word follows it
   ("The Willow Co." -> WILL, but "The Hawthorn" -> THEH). Allowed characters
   are A-Z, 0-9, hyphen and ampersand (efileTypes.xsd BusinessNameControlType). */
export const deriveCtl = (name) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  const rest = words.length > 2 && /^the$/i.test(words[0]) ? words.slice(1) : words;
  return rest.join("").replace(/[^A-Za-z0-9&-]/g, "").toUpperCase().slice(0, 4);
};

/* Name-control choices shown to sole proprietors: one candidate per word of the
   name. IRS Pub 4164 Exhibit 4 d) bases a sole proprietor's control on the last
   name, and Exhibit 5 picks which surname (two Spanish surnames -> the FIRST,
   e.g. "Juan Garza Morales" -> GARZ; other double surnames -> the second) - which
   word that is cannot be told from the name alone, so the filer picks.
   Exhibit 5 keeps a surname prefix with the name: "Eduardo de la Rosa" -> DELA,
   "Mary J. Van Elm" -> VANE, so a prefix after the first word is joined to the
   word that follows. "DBA" is never part of a control (p.54); single letters
   (middle initials) are skipped. */
const SURNAME_PREFIXES = ["de", "del", "la", "van", "von", "vander", "al", "el", "abu", "di"];
export const ctlSuggestions = (name) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  const out = [];
  let prefix = "";
  words.forEach((w, i) => {
    if (/^dba$/i.test(w)) { prefix = ""; return; }
    if (i > 0 && i < words.length - 1 && SURNAME_PREFIXES.includes(w.toLowerCase())) {
      prefix += w;
      return;
    }
    const ctl = (prefix + w).replace(/[^A-Za-z0-9&-]/g, "").toUpperCase().slice(0, 4);
    prefix = "";
    if (ctl.length > 1 && !out.includes(ctl)) out.push(ctl);
  });
  return out;
};

// Live-typing filter for the name control input.
export const sanitizeCtlInput = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9&-]/g, "").slice(0, 4);

export const isValidEin = (v) => /^\d{2}-?\d{7}$/.test((v || "").trim());
export const isValidZip = (v) => /^\d{5}(-?(\d{4}|\d{7}))?$/.test((v || "").trim());
export const isValidVin = (v) => /^[A-Z0-9]{17}$|^[A-Z0-9]{19}$/.test((v || "").trim().toUpperCase());
// Live-typing filter for every VIN input - uppercases and strips anything
// that isn't a letter or digit (no spaces, hyphens, or other symbols), so
// the field only ever holds what isValidVin can accept.
export const sanitizeVinInput = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
export const isValidPhone = (v) => /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test((v || "").trim());
// Matches efileTypes.xsd's EmailAddressType (Business/Tax Preparer email
// fields): printable ASCII (no accented/currency characters, unlike
// ExplanationType) plus single spaces, and a local@domain.tld shape. Length
// / single-@ / no-leading-trailing-space checks live in each screen's RULES
// so a violation gets its own message.
const EMAIL_CHARS = /^[\x21-\x7E ]+$/;
export const isValidIrsEmail = (v) => {
  const val = (v || "").trim();
  return /^.+@.+\..+$/.test(val) && EMAIL_CHARS.test(val);
};
// Real-world email check, used by the Login/signup form. Same printable-ASCII
// character set as isValidIrsEmail above (minus spaces, which have no place
// in a login email) so uppercase letters and punctuation like ( ) : ; < > [ ] \ ,
// are accepted here too, matching the Business/Tax Preparer screens.
const LOGIN_EMAIL_CHARS = /^[\x21-\x7E]+$/;
export const isValidEmail = (v) => {
  const val = (v || "").trim();
  return (
    (val.match(/@/g) || []).length === 1 &&
    /^.+@.+\..+$/.test(val) &&
    LOGIN_EMAIL_CHARS.test(val)
  );
};
// Live-typing filter for Business/Tax Preparer email fields - strips
// anything outside the EmailAddressType character set, collapses repeated
// spaces to one, and caps at the 75-char maximum, so the field only ever
// holds what isValidIrsEmail (plus the length/spacing checks in RULES) can
// accept.
export const sanitizeEmailInput = (v) =>
  (v || "")
    .replace(/[^\x21-\x7E ]/g, "")
    .replace(/^ /, "")
    .replace(/ {2,}/g, " ")
    .slice(0, 75);
export const isValidPtin = (v) => /^P\d{8}$/.test((v || "").trim().toUpperCase());
export const isValidPin5 = (v) => /^\d{5}$/.test((v || "").trim());

// Matches efileTypes.xsd's ExplanationType (TextType restricted to
// maxLength 9000): printable ASCII plus the handful of accented/currency
// characters TextType allows, no leading/trailing space, no consecutive
// spaces. Used for the VIN Correction Explanation Statement.
const EXPLANATION_CHARS = /^[\x21-\x7E£§ÁÉÍÑÓ×ÚÜáéíñóúü ]+$/;

// Matches efileTypes.xsd's TextType (used by ProvinceOrStateNm/ForeignPostalCd
// on Canada/Mexico addresses): printable ASCII #x21-#x7E plus the same
// handful of accented/currency characters as ExplanationType, single spaces
// only. TEXT_TYPE_STRIP is the live-typing companion - strips anything
// outside that set so the field only ever holds what TEXT_TYPE_CHARS accepts.
const TEXT_TYPE_CHARS = /^[\x21-\x7E£§ÁÉÍÑÓ×ÚÜáéíñóúü ]+$/;
export const TEXT_TYPE_STRIP = /[^\x21-\x7E£§ÁÉÍÑÓ×ÚÜáéíñóúü ]/g;

// Live-typing sanitizer for Explanation fields (VIN Correction Explanation
// Statement, Credits Explanation) - collapses runs of spaces to one and
// strips a leading space as it's typed, so consecutive spaces can never
// even be entered. Trailing space is left for onBlur/save (see
// validateExplanation and each screen's save path) to trim, so the user
// isn't fighting the field mid-word.
export const sanitizeExplanationInput = (v) => (v || "").replace(/^ /, "").replace(/ {2,}/g, " ");

export const validateExplanation = (v, max = 9000) => {
  const val = v || "";
  if (!val.trim()) return "An explanation is required.";
  if (val.length > max) return `Maximum ${max} characters allowed.`;
  if (val !== val.trimStart()) return "Cannot start with a space.";
  if (val !== val.trimEnd()) return "Cannot end with a space.";
  if (/ {2}/.test(val)) return "No consecutive spaces allowed.";
  if (!EXPLANATION_CHARS.test(val)) return "Only standard printable characters are allowed.";
  return "";
};

const NAME_CHARS = /^[A-Za-z0-9#()&'\- ]+$/;
const ADDRESS_CHARS = /^[A-Za-z0-9\-/ ]+$/;
const CITY_CHARS = /^[A-Za-z ]+$/;

const hasBadSpacing = (v) => v !== v.trim() || / {2}/.test(v);

export const isValidName = (v, max = 75) => {
  const val = v || "";
  if (!val.trim() || val.length > max || hasBadSpacing(val)) return false;
  return NAME_CHARS.test(val);
};

// Matches efileTypes.xsd's BusinessNameLine1Type (buyer/transferee name on
// Part II line 9 maps to BusinessNameLine1Txt in Return2290Assembler, same
// as PriorYearServiceImpl's BUYER_NAME_PATTERN on the backend): letters,
// digits, # ( ) & ' - and single interior spaces, max 75.
export const validateBuyerTransfereeName = (v) => {
  const val = v || "";
  if (!val.trim()) return "Buyer / transferee name is required.";
  if (val.length > 75) return "Maximum 75 characters allowed.";
  if (val !== val.trimStart()) return "Cannot start with a space.";
  if (val !== val.trimEnd()) return "Cannot end with a space.";
  if (/ {2}/.test(val)) return "No consecutive spaces allowed.";
  if (!NAME_CHARS.test(val)) return "Only letters, numbers, and # ( ) & ' - are allowed.";
  return "";
};

export const isValidStreetAddress = (v, max = 35) => {
  const val = v || "";
  if (!val.trim() || val.length > max || hasBadSpacing(val)) return false;
  if (/^[-/]/.test(val) || /[-/]$/.test(val)) return false;
  return ADDRESS_CHARS.test(val);
};

export const isValidCity = (v, max = 22) => {
  const val = v || "";
  if (!val.trim() || val.length > max || hasBadSpacing(val)) return false;
  return CITY_CHARS.test(val);
};

// ── Format hint text shown under each field (matches BusinessesScreen) ──
export const FIELD_FORMATS = {
  name: "Letters, numbers, and # ( ) & ' - · Single spaces only · Max 60",
  dba: "Letters, numbers, and # ( ) & ' - · Single spaces only · Max 75",
  buyerTransferee: "Letters, numbers, and # ( ) & ' - · Single spaces only · Max 75",
  ein: "Format: XX-XXXXXXX or 9 digits — dash optional",
  ctl: "1 to 4 characters · Letters, numbers, - and & · e.g. RAYS",
  email: "Format: name@company.com · Single spaces only · Max 75",
  phone: "Format: (XXX) XXX-XXXX",
  signingAuthorityName: "Letters, digits, apostrophes, hyphens, spaces · Max 35",
  signingAuthorityTitle: "Letters, numbers, and standard punctuation · Max 35",
  signingAuthorityPhone: "Format: (XXX) XXX-XXXX",
  a1: "Letters, numbers, hyphens, and / only · Max 35",
  a2: "Letters, numbers, hyphens, and / only · Max 35",
  city: "Letters and spaces only · Max 22",
  state: "Letters and spaces only · Max 22",
  zip: "Format: 12345 · 12345-6789 · 12345-6789012",
  tpdName: "Letters, numbers, apostrophes, hyphens, spaces · Max 35",
  tpdPhone: "Format: (XXX) XXX-XXXX",
  tpdPin: "Exactly 5 numeric digits",
};

/* ── Rules return a SPECIFIC message string, or "" if valid ──
   This mirrors the RULES object in BusinessesScreen exactly, so the same
   field problem always produces the same visible message + red border. */
// Form 2290 and Schedule 1 each print a separate "Room or suite" box, and the
// PDF fills it separately - but the e-file cannot carry two lines. Business rule
// R0000-197-01 (Active, Reject) bars AddressLine2Txt from the Filer group, so
// the API joins address lines 1 and 2 with a single space into AddressLine1Txt,
// a StreetAddressType capped at 35 characters. Street and suite therefore SHARE
// those 35; each box allowing 35 on its own is not enough to keep the pair
// transmissible. Filer/business only - a preparer's AddressLine2Txt is a
// different group, is emitted normally, and must NOT be length-checked this way.
const STREET_LINE_MAX = 35;

const combinedStreetError = (biz) => {
  const l1 = (biz?.a1 || "").trim();
  const l2 = (biz?.a2 || "").trim();
  if (!l1 || !l2) return ""; // nothing to combine - per-field rules already apply
  const len = (l1 + " " + l2).length;
  if (len > STREET_LINE_MAX) {
    return `Address line 1 and line 2 are combined into one line on the filing, and together they can be at most ${STREET_LINE_MAX} characters. Yours currently uses ${len}. Try shortening one - for example "Ste" instead of "Suite".`;
  }
  return "";
};

const RULES = {
  name: (v) => {
    if (!v.trim()) return "Business name is required.";
    if (v.length > 60) return "Maximum 60 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!NAME_CHARS.test(v)) return "Only letters, numbers, and # ( ) & ' - are allowed.";
    return "";
  },
  dba: (v) => {
    if (!v.trim()) return ""; // optional
    if (v.length > 75) return "Maximum 75 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!NAME_CHARS.test(v)) return "Only letters, numbers, and # ( ) & ' - are allowed.";
    return "";
  },
  ein: (v) => {
    if (!v.trim()) return "EIN is required.";
    if (!/^\d{9}$/.test(v.replace(/-/g, ""))) return "EIN must be exactly 9 numeric digits.";
    return "";
  },
  businessType: (v) => (!v ? "Pick a business type." : ""),
  ctl: (v) => {
    if (!v.trim()) return "Name control is required. You can find it on your IRS EIN letter.";
    if (!/^[A-Z0-9&-]{1,4}$/.test(v)) return "Use 1 to 4 characters: letters, numbers, hyphen or ampersand.";
    return "";
  },
  email: (v) => {
    if (!v.trim()) return "Email is required.";
    if (v !== v.trim()) return "Email cannot have leading or trailing spaces.";
    if (v.length > 75) return "Maximum 75 characters allowed.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if ((v.match(/@/g) || []).length !== 1) return "Email must contain exactly one @.";
    if (!isValidIrsEmail(v)) return "Invalid email format.";
    return "";
  },
  phone: (v) => {
    if (!v.trim()) return "Business phone is required.";
    if (!isValidPhone(v)) return "Enter a valid 10-digit phone number.";
    if (/^\D*0{10}\D*$/.test(v)) return "Phone cannot be all zeros.";
    return "";
  },
  signingAuthorityName: (v) => {
    if (!v.trim()) return "Signing authority name is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!/^[A-Za-z0-9'\- ]+$/.test(v))
      return "Only letters, digits, apostrophes, hyphens, and spaces are allowed.";
    if (/^-/.test(v)) return "Name cannot start with a hyphen.";
    if (/-$/.test(v)) return "Name cannot end with a hyphen.";
    return "";
  },
  signingAuthorityTitle: (v) => {
    if (!v.trim()) return "Title is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    return "";
  },
  signingAuthorityPhone: (v) => {
    if (!v.trim()) return "Signing authority phone is required.";
    if (!isValidPhone(v)) return "Enter a valid 10-digit phone number.";
    if (/^\D*0{10}\D*$/.test(v)) return "Phone cannot be all zeros.";
    return "";
  },
  a1: (v, biz) => {
    if (!v.trim()) return "Address line 1 is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (/^[-/]/.test(v)) return "Address cannot start with - or /.";
    if (/[-/]$/.test(v)) return "Address cannot end with - or /.";
    if (!ADDRESS_CHARS.test(v)) return "Only letters, numbers, hyphens, and / are allowed.";
    return combinedStreetError({ ...biz, a1: v });
  },
  a2: (v, biz) => {
    if (!v.trim()) return ""; // optional
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (/^[-/]/.test(v)) return "Address cannot start with - or /.";
    if (/[-/]$/.test(v)) return "Address cannot end with - or /.";
    if (!ADDRESS_CHARS.test(v)) return "Only letters, numbers, hyphens, and / are allowed.";
    return combinedStreetError({ ...biz, a2: v });
  },
  country: (v) => (!v ? "Pick a country." : ""),
  city: (v, biz) => {
    const isUs = !biz || !biz.country || biz.country === "US";
    const maxLen = isUs ? 22 : 50;
    if (!v.trim()) return "City is required.";
    if (v.length > maxLen) return `Maximum ${maxLen} characters allowed.`;
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!CITY_CHARS.test(v)) return "Only letters and spaces are allowed.";
    return "";
  },
  // Non-US (Canada/Mexico): free-text State/Province - IRS TextType, max 17.
  state: (v, biz) => {
    const isUs = !biz || !biz.country || biz.country === "US";
    if (isUs) return !v ? "Pick a state." : "";
    if (!v.trim()) return "State / Province is required.";
    if (v.length > 17) return "Maximum 17 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!TEXT_TYPE_CHARS.test(v)) return "Invalid characters detected.";
    return "";
  },
  // US: existing numeric ZIP pattern (unchanged). Canada/Mexico: IRS TextType, max 16.
  zip: (v, biz) => {
    const isUs = !biz || !biz.country || biz.country === "US";
    if (!v.trim()) return "ZIP is required.";
    if (isUs) {
      if (!isValidZip(v)) return "Invalid ZIP code. Use 5, 9 (XXXXX-XXXX), or 12 (XXXXX-XXXXXXX) digits.";
      return "";
    }
    if (v.length > 16) return "Maximum 16 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!TEXT_TYPE_CHARS.test(v)) return "Invalid characters detected.";
    return "";
  },
};

const TPD_RULES = {
  tpdName: (v) => {
    if (!v.trim()) return "Designee name is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!/^[A-Za-z0-9'\- ]+$/.test(v))
      return "Only letters, numbers, apostrophes, hyphens, and spaces are allowed.";
    return "";
  },
  tpdPhone: (v) => {
    if (!v.trim()) return "Designee phone is required.";
    if (!isValidPhone(v)) return "Enter a valid 10-digit phone number.";
    if (/^\D*0{10}\D*$/.test(v)) return "Phone cannot be all zeros.";
    return "";
  },
  tpdPin: (v) => {
    if (!v) return "PIN is required.";
    if (!isValidPin5(v)) return "PIN must be exactly 5 numeric digits.";
    return "";
  },
};

export function validateBiz(biz) {
  const errors = {};
  Object.entries(RULES).forEach(([key, rule]) => {
    const msg = rule(biz[key] ?? "", biz);
    if (msg) errors[key] = msg;
  });
  if (biz.hasThirdPartyDesignee) {
    Object.entries(TPD_RULES).forEach(([key, rule]) => {
      const msg = rule(biz[key] ?? "", biz);
      if (msg) errors[key] = msg;
    });
  }
  const ok = Object.keys(errors).length === 0;
  return { ok, errors }; // errors[key] is now a STRING (or absent if valid)
}

// ── Format hints for TaxPreparerStep, mirrors PreparersScreen's FIELD_FORMATS ──
export const FIELD_FORMATS_PREPARER = {
  name: "Letters, numbers, apostrophe, hyphen · Single spaces only · Max 35",
  ptin: "Format: P followed by exactly 8 digits · e.g. P12345678",
  email: "Format: name@company.com · Single spaces only · Max 75",
  phone: "Format: (XXX) XXX-XXXX",
  ext: "Digits only · Exactly 3 characters · e.g. 101",
  firmName:
    "Letters, numbers, and # ( ) & ' - · Single spaces only · Max 60 · Cannot start or end with a special character",
  firmEin: "Format: XX-XXXXXXX (9 digits)",
  country: "Choose the country for the preparer address.",
  a1: "Letters, numbers, hyphens, and / only · Max 35",
  a2: "Optional suite, unit, or building details.",
  city: "Letters and spaces only · Max 22",
  state: "Pick a U.S. state or type a province/state for a foreign address.",
  zip: "Format: 12345 · 12345-6789 · 12345-6789012",
};

const phoneDigitsOnly = (v) => (v || "").replace(/\D/g, "");

/* Rules mirror PreparersScreen's RULES/FIRM_RULES exactly, returning a
   message string (or "" when valid) instead of a boolean. */
const PREP_RULES = {
  name: (v) => {
    if (!v.trim()) return "Tax preparer name is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (/--/.test(v)) return "No consecutive hyphens allowed.";
    if (!/^[A-Za-z0-9'\- ]+$/.test(v))
      return "Only letters, numbers, apostrophes, and hyphens allowed.";
    if (/^[-']/.test(v)) return "Cannot start with a hyphen or apostrophe.";
    if (/[-']$/.test(v)) return "Cannot end with a hyphen or apostrophe.";
    return "";
  },
  ptin: (v) => {
    if (!v.trim()) return "PTIN is required.";
    if (!/^P\d{8}$/i.test(v)) return "PTIN must be P followed by exactly 8 digits.";
    return "";
  },
  email: (v) => {
    if (!v.trim()) return "Email is required.";
    if (v !== v.trim()) return "Email cannot have leading or trailing spaces.";
    if (v.length > 75) return "Maximum 75 characters allowed.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if ((v.match(/@/g) || []).length !== 1) return "Email must contain exactly one @.";
    if (!isValidIrsEmail(v)) return "Invalid email format.";
    return "";
  },
  phone: (v) => {
    const d = phoneDigitsOnly(v);
    if (!d) return "Phone is required.";
    if (!/^\d{10}$/.test(d)) return "Enter a valid 10-digit phone number.";
    if (/^0{10}$/.test(d)) return "Phone cannot be all zeros.";
    return "";
  },
  country: (v) => (!v ? "Pick a country." : ""),
  ext: (v) => {
    if (!v) return ""; // optional
    if (!/^\d{3}$/.test(v)) return "Extension must be exactly 3 digits.";
    return "";
  },
  a1: (v) => {
    if (!v.trim()) return "Address is required.";
    if (v.length > 35) return "Maximum 35 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (/^[-/]/.test(v)) return "Address cannot start with - or /.";
    if (/[-/]$/.test(v)) return "Address cannot end with - or /.";
    if (!/^[A-Za-z0-9\-/ ]+$/.test(v)) return "Only letters, numbers, hyphens, and / are allowed.";
    return "";
  },
  city: (v, preparer) => {
    const isUs = !preparer || !preparer.country || preparer.country === "US";
    const maxLen = isUs ? 22 : 50;
    if (!v.trim()) return "City is required.";
    if (v.length > maxLen) return `Maximum ${maxLen} characters allowed.`;
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!/^[A-Za-z ]+$/.test(v)) return "Only letters and spaces are allowed.";
    return "";
  },
  // Non-US (Canada/Mexico): free-text State/Province - IRS TextType, max 17.
  state: (v, preparer) => {
    const isUs = !preparer || !preparer.country || preparer.country === "US";
    if (isUs) return !v ? "Pick a state." : "";
    if (!v.trim()) return "State / Province is required.";
    if (v.length > 17) return "Maximum 17 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!TEXT_TYPE_CHARS.test(v)) return "Invalid characters detected.";
    return "";
  },
  // US: existing numeric ZIP pattern (unchanged). Canada/Mexico: IRS TextType, max 16.
  zip: (v, preparer) => {
    const isUs = !preparer || !preparer.country || preparer.country === "US";
    if (!v.trim()) return "ZIP is required.";
    if (isUs) {
      if (!/^\d{5}$|^\d{5}-\d{4}$|^\d{5}-\d{7}$/.test(v.trim()))
        return "Invalid ZIP code. Use 5, 9 (XXXXX-XXXX), or 12 (XXXXX-XXXXXXX) digits.";
      return "";
    }
    if (v.length > 16) return "Maximum 16 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!TEXT_TYPE_CHARS.test(v)) return "Invalid characters detected.";
    return "";
  },
};

const PREP_FIRM_RULES = {
  firmName: (v) => {
    if (!v.trim()) return "Firm name is required.";
    if (v.length > 60) return "Maximum 60 characters allowed.";
    if (v !== v.trimStart()) return "Cannot start with a space.";
    if (v !== v.trimEnd()) return "Cannot end with a space.";
    if (/ {2}/.test(v)) return "No consecutive spaces allowed.";
    if (!/^[A-Za-z0-9#()&'\- ]+$/.test(v))
      return "Only letters, numbers, and # ( ) & ' - are allowed.";
    if (/^[#()&'-]/.test(v)) return "Cannot start with a special character.";
    if (/[#()&'-]$/.test(v)) return "Cannot end with a special character.";
    return "";
  },
  firmEin: (v) => {
    if (!v.trim()) return "EIN is required.";
    if (!/^\d{9}$/.test(v.replace(/-/g, ""))) return "EIN must be exactly 9 numeric digits.";
    return "";
  },
};

export function validatePreparer(preparer) {
  const errors = {};
  Object.entries(PREP_RULES).forEach(([key, rule]) => {
    const msg = rule(preparer[key] ?? "", preparer);
    if (msg) errors[key] = msg;
  });
  if (preparer.isFirmEmployee) {
    Object.entries(PREP_FIRM_RULES).forEach(([key, rule]) => {
      const msg = rule(preparer[key] ?? "", preparer);
      if (msg) errors[key] = msg;
    });
  }
  const ok = Object.keys(errors).length === 0;
  return { ok, errors }; // errors[key] is a message STRING
}